import { value } from '@takeshape/util';
import urlJoin from 'url-join';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { getAuthInfoCacheExpirationForClientCredentials, getAuthInfoRequestConfigForClientCredentials, getAuthInfoRequestResultsForClientCredentials } from "../util/auth-info.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { fixObfuscatedProp } from "../util/props.js";
const title = 'Elastic Path';
const serviceProvider = {
    name: 'elasticpath',
    title,
    description: 'Elastic Path Commerce Cloud PCM API',
    categories: ['fun'],
    links: [{ title: 'Homepage', url: 'https://www.elasticpath.com/' }],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('elasticpath'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$',
                default: 'elastic-path'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: 'Elastic Path'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'ElasticPath'
            },
            endpoint: {
                title: 'Base Url',
                type: 'string',
                format: 'url'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    clientId: {
                        title: 'Client ID',
                        type: 'string',
                        '@sensitive': true
                    },
                    clientSecret: {
                        title: 'Client Secret',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['clientId', 'clientSecret']
            }
        },
        required: ['title', 'id', 'namespace', 'endpoint', 'authentication']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'endpoint', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            endpoint: {
                widget: 'singleLineText',
                instructions: 'Each customer’s Elastic Path Commerce Cloud Commerce Manager home page provides the correct store base URL in the API keys > API base URL field.'
            },
            authentication: {
                order: ['clientId', 'clientSecret'],
                properties: {
                    clientId: {
                        widget: 'sensitiveField'
                    },
                    clientSecret: {
                        widget: 'sensitiveField'
                    }
                }
            }
        }
    }),
    getConfig(formData) {
        const endpoint = formData.endpoint;
        const config = {
            title: formData.title ?? 'Elastic Path',
            namespace: formData.namespace ?? 'ElasticPath',
            provider: 'elasticpath',
            serviceType: 'openapi',
            authenticationType: 'oauth2',
            options: {
                endpoint,
                spec: 'https://raw.githubusercontent.com/takeshape/openapi-specs/main/specs/elasticpath/pcm.yaml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        const { authentication } = formData;
        if (!authentication) {
            return config;
        }
        config.authentication = {
            type: 'oauth2',
            grantType: 'clientCredentials',
            accessTokenUrl: urlJoin(endpoint, '/oauth/access_token'),
            clientId: fixObfuscatedProp(authentication.clientId),
            clientSecret: fixObfuscatedProp(authentication.clientSecret)
        };
        return config;
    },
    getAuthInfoCacheExpiration: getAuthInfoCacheExpirationForClientCredentials(title),
    getAuthInfoRequestConfig: getAuthInfoRequestConfigForClientCredentials(title),
    getAuthInfoResult: getAuthInfoRequestResultsForClientCredentials(title),
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint']),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/pcm/catalogs')
    })
};
export default serviceProvider;
